import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { shatterSfx, startFocusAmbience } from '../../../utils/ritualSfx';
import { DISTRACTIONS } from './oathTheme';

const DURATION = 45;
let uid = 0;

/* 45-second focus game: distractions drift in, tap to shatter them while the laser holds your focus. */
export const LockInGame = ({ focus, onFinish, onQuit }) => {
  const [left, setLeft] = useState(DURATION);
  const [items, setItems] = useState([]);
  const [shards, setShards] = useState([]);
  const [score, setScore] = useState(0);
  const scoreRef = useRef(0);

  useEffect(() => startFocusAmbience(DURATION), []);
  useEffect(() => {
    const clock = setInterval(() => setLeft((s) => s - 1), 1000);
    const spawn = setInterval(() => {
      setItems((cur) => [...cur.slice(-7), { id: ++uid, word: DISTRACTIONS[Math.floor(Math.random() * DISTRACTIONS.length)], x: 8 + Math.random() * 70, y: 22 + Math.random() * 58, born: Date.now() }]);
    }, 900);
    return () => { clearInterval(clock); clearInterval(spawn); };
  }, []);

  const done = useRef(false);
  useEffect(() => { if (left <= 0 && !done.current) { done.current = true; onFinish(scoreRef.current); } }, [left, onFinish]);
  useEffect(() => {
    const t = setInterval(() => setItems((cur) => cur.filter((i) => Date.now() - i.born < 3800)), 500);
    return () => clearInterval(t);
  }, []);

  const hit = (it) => {
    shatterSfx();
    scoreRef.current += 1;
    setScore(scoreRef.current);
    setItems((cur) => cur.filter((i) => i.id !== it.id));
    setShards((cur) => [...cur, { id: it.id, x: it.x, y: it.y }]);
    setTimeout(() => setShards((cur) => cur.filter((s) => s.id !== it.id)), 600);
  };

  return (
    <motion.div className="fixed inset-0 z-[300] overflow-hidden select-none" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ background: 'radial-gradient(ellipse at 50% 50%, #08142A, #020308 75%)', touchAction: 'manipulation' }} data-testid="oath-lockin-game">
      <motion.div className="absolute left-0 right-0 h-[2px]" animate={{ top: ['18%', '88%', '18%'] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        style={{ background: 'linear-gradient(90deg, transparent, #5DD4FF, #FFFFFF, #5DD4FF, transparent)', boxShadow: '0 0 18px #5DD4FF, 0 0 40px #5DD4FF' }} />
      <div className="absolute top-0 left-0 right-0 px-5 flex items-center justify-between" style={{ paddingTop: 'max(var(--safe-area-inset-top, 0px), 16px)' }}>
        <button type="button" onClick={onQuit} className="font-['Outfit'] text-[10px] tracking-[0.2em] uppercase text-white/40" data-testid="oath-lockin-quit">Quit</button>
        <p className="font-['Outfit'] text-2xl font-semibold" style={{ color: '#D4F3FF' }} data-testid="oath-lockin-timer">{Math.max(0, left)}s</p>
        <p className="font-['Outfit'] text-sm" style={{ color: '#5DD4FF' }} data-testid="oath-lockin-score">Shattered {score}</p>
      </div>
      <div className="absolute left-0 right-0 text-center px-6" style={{ top: '46%' }}>
        <motion.div animate={{ scale: [1, 1.04, 1] }} transition={{ duration: 2, repeat: Infinity }}
          className="inline-block rounded-2xl px-5 py-3" style={{ background: 'rgba(93,212,255,0.08)', border: '1px solid rgba(93,212,255,0.6)', boxShadow: '0 0 30px rgba(93,212,255,0.4)' }}>
          <p className="font-['Outfit'] text-[9px] tracking-[0.35em] uppercase text-[#5DD4FF]">Locked on</p>
          <p className="font-['Cormorant_Garamond'] italic text-2xl text-white mt-1">{focus}</p>
        </motion.div>
        <p className="font-['Outfit'] text-[11px] text-white/40 mt-3">Tap every distraction to shatter it</p>
      </div>
      <AnimatePresence>
        {items.map((it) => (
          <motion.button key={it.id} type="button" onPointerDown={() => hit(it)} data-testid="oath-distraction"
            className="absolute px-3 py-1.5 rounded-full font-['Outfit'] text-sm"
            initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 0.85, scale: 1, x: [0, 8, -8, 0] }} exit={{ opacity: 0, scale: 0.2 }}
            transition={{ x: { duration: 2, repeat: Infinity } }}
            style={{ left: `${it.x}%`, top: `${it.y}%`, background: 'rgba(255,100,120,0.15)', border: '1px solid rgba(255,100,120,0.5)', color: '#FFB3BF', filter: 'blur(0.4px)' }}>
            {it.word}
          </motion.button>
        ))}
      </AnimatePresence>
      {shards.map((s) => (
        <div key={s.id} className="absolute pointer-events-none" style={{ left: `${s.x + 4}%`, top: `${s.y + 1}%` }}>
          {[0, 60, 120, 180, 240, 300].map((a) => (
            <motion.span key={a} className="absolute w-2 h-0.5 bg-[#5DD4FF]" initial={{ x: 0, y: 0, opacity: 1 }}
              animate={{ x: Math.cos(a * Math.PI / 180) * 40, y: Math.sin(a * Math.PI / 180) * 40, opacity: 0, rotate: a }} transition={{ duration: 0.5 }}
              style={{ boxShadow: '0 0 6px #5DD4FF' }} />
          ))}
        </div>
      ))}
    </motion.div>
  );
};

export default LockInGame;
